import { Link } from 'react-router-dom';
import { PCard } from './PCard';
import { StatusPill } from './StatusPill';
import { invoiceStatusBadge, FLOW_META } from '../lib/types';
import { invoicePdfUrl, CARD_PAYMENT_MAX_CENTS, type InvoiceRow } from '../lib/api';
import { backState, type BackTo } from '../lib/backTo';
import { fmtCents, fmtDate } from '../lib/format';

const shortId = (id: string) => `#${id.slice(-6)}`;
const CHARGEABLE = new Set(['open', 'past_due', 'partial']);

export const InvoiceTable = ({
  invoices,
  backTo,
  showClient = true,
  showType = true,
  onCharge,
  onMarkPaid,
  onPayLink,
  onAdjust,
  onRefund,
  busyId,
}: {
  invoices: InvoiceRow[];
  /** Where the invoice detail page should send the user back to, i.e. this list. */
  backTo?: BackTo;
  showClient?: boolean;
  showType?: boolean;
  /** Shown for auto_charge invoices with a balance. */
  onCharge?: (inv: InvoiceRow) => void;
  /** Shown for wire/ACH invoices with a balance. */
  onMarkPaid?: (inv: InvoiceRow) => void;
  /** Copy a hosted pay link for an invoice with a balance. */
  onPayLink?: (inv: InvoiceRow) => void;
  /** Add a credit/fee line — only for unpaid invoices. */
  onAdjust?: (inv: InvoiceRow) => void;
  /** Refund a paid invoice via its rail. */
  onRefund?: (inv: InvoiceRow) => void;
  busyId?: string | null;
}) => (
  <PCard>
    <table className="p-table">
      <thead>
        <tr>
          <th>Invoice</th>
          {showClient && <th>Client</th>}
          {showType && <th>Type</th>}
          <th>Period</th>
          <th>Amount</th>
          <th>Status</th>
          <th>Due</th>
          <th style={{ textAlign: 'right' }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {invoices.map((inv) => {
          const s = invoiceStatusBadge(inv.status, inv.voidKind);
          const chargeable = CHARGEABLE.has(inv.status) && inv.amountDueCents > 0;
          const wireAch = inv.collectionMethod !== 'auto_charge';
          // >= $20k: card payments (charge + pay link) are off; wire/ACH + mark paid only.
          const cardOk = inv.totalCents < CARD_PAYMENT_MAX_CENTS;
          const busy = busyId === inv._id;
          return (
            <tr key={inv._id}>
              <td className="mono">
                <Link to={`/invoices/${inv._id}`} state={backTo && backState(backTo)}>
                  {inv.invoiceNumber ?? shortId(inv._id)}
                </Link>
              </td>
              {showClient && <td>{inv.clientName}</td>}
              {showType && (
                <td className="muted">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: FLOW_META[inv.flow].color }} />
                    {FLOW_META[inv.flow].label}
                  </span>
                </td>
              )}
              <td className="muted">{inv.period.label}</td>
              <td className="tabular">{fmtCents(inv.totalCents)}</td>
              <td>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <StatusPill tone={s.tone}>{s.label}</StatusPill>
                  {wireAch && (
                    <span className="muted" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                      {inv.collectionMethod}
                    </span>
                  )}
                </span>
              </td>
              <td className="muted">{inv.dueAt ? fmtDate(inv.dueAt) : '—'}</td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
                  <a
                    className="btn"
                    href={invoicePdfUrl(inv._id)}
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    PDF
                  </a>
                  {chargeable && !wireAch && cardOk && onCharge && (
                    <button className="btn" disabled={busy} onClick={() => onCharge(inv)}>
                      {busy ? '…' : 'Charge'}
                    </button>
                  )}
                  {chargeable && (wireAch || !cardOk) && onMarkPaid && (
                    <button className="btn" disabled={busy} onClick={() => onMarkPaid(inv)}>
                      {busy ? '…' : 'Mark paid'}
                    </button>
                  )}
                  {chargeable && cardOk && onPayLink && (
                    <button className="btn" onClick={() => onPayLink(inv)}>Pay link</button>
                  )}
                  {onAdjust && inv.status !== 'void' && inv.status !== 'paid' && (
                    <button className="btn" disabled={busy} onClick={() => onAdjust(inv)}>Adjust</button>
                  )}
                  {onRefund && inv.status === 'paid' && inv.amountPaidCents > 0 && (
                    <button className="btn" disabled={busy} onClick={() => onRefund(inv)}>
                      {busy ? '…' : 'Refund'}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </PCard>
);
